// Player stats — pure utility, no framework deps. Persisted in localStorage.
//
// Tracked counters (all non-negative integers):
//   - clearedFields:  cells the player successfully revealed across all levels
//   - minesDefused:   sum of mines on every level the player CLEARED (won)
//   - totalPlayTime:  total seconds spent inside actual level runs
//   - totalGameOvers: number of level losses (after any continue is declined / used up)
//   - levelsStarted:  every fresh level run, including retries after a loss
//   - levelsWon:      successful level clears
//   - firstTryWins:   wins on a level the player never failed AND never used Continue on
//
// Per-level transient flags (cleared once a level is won):
//   - currentLevelTracked: the level number the first-try flag refers to
//   - currentLevelFirstTry: '1' while the current level is still a clean attempt
//
// Derived stats are computed in getStats() against the existing level-progress
// counter ('minesweeper_cleared') so we never duplicate the source of truth.
//
// Note: the existing 'minesweeper_cleared' key (managed by GameState.js) stores
// the highest level CLEARED, so currentLevel = cleared + 1 (capped at 999).

import { isStatsEnabled } from './settings.js';
import { getItem, setItem, removeItem } from './storage.js';

const KEY_CLEARED_FIELDS  = 'minesweeper_stats_cleared_fields';
const KEY_MINES_DEFUSED   = 'minesweeper_stats_mines_defused';
const KEY_PLAY_TIME       = 'minesweeper_stats_play_time';
const KEY_GAME_OVERS      = 'minesweeper_stats_game_overs';
const KEY_LEVELS_STARTED  = 'minesweeper_stats_levels_started';
const KEY_LEVELS_WON      = 'minesweeper_stats_levels_won';
const KEY_FIRST_TRY_WINS  = 'minesweeper_stats_first_try_wins';
const KEY_CUR_LEVEL       = 'minesweeper_stats_current_level_tracked';
const KEY_CUR_FIRST_TRY   = 'minesweeper_stats_current_level_first_try';

const KEY_LEVEL_PROGRESS  = 'minesweeper_cleared'; // existing key (read-only here)

const MAX_LEVEL = 999;


function readInt( key )
{
    const raw = getItem( key );
    if ( raw === null ) return 0;
    const n = parseInt( raw, 10 );
    return Number.isFinite( n ) && n >= 0 ? n : 0;
}

function writeInt( key, value )
{
    setItem( key, String( Math.max( 0, Math.floor( value ) ) ) );
}

function addInt( key, delta )
{
    if ( !Number.isFinite( delta ) || delta <= 0 ) return readInt( key );
    const next = readInt( key ) + Math.floor( delta );
    writeInt( key, next );
    return next;
}


/**
 * Record the start of a level run. Increments the levelsStarted counter and,
 * when the level number changes vs. the previously tracked level, resets the
 * first-try flag to true. Called from resetGame() — every retry counts.
 */
export function recordLevelStarted( levelNumber )
{
    if ( !isStatsEnabled() ) return;

    addInt( KEY_LEVELS_STARTED, 1 );

    const tracked = parseInt( getItem( KEY_CUR_LEVEL ) || '0', 10 );
    if ( tracked !== levelNumber )
    {
        setItem( KEY_CUR_LEVEL, String( levelNumber ) );
        setItem( KEY_CUR_FIRST_TRY, '1' );
    }
}

/**
 * Mark the current level as no-longer-a-first-try. Called when the player uses
 * the Continue revive — even though they survive, the win shouldn't count as
 * "first try clean".
 */
export function markCurrentLevelNotFirstTry()
{
    if ( !isStatsEnabled() ) return;
    setItem( KEY_CUR_FIRST_TRY, '0' );
}

/**
 * Record a level win. clearedCells is the number of cells the player revealed
 * legitimately, mines is the total mines on the board, elapsedSeconds is the
 * play time for this level run.
 */
export function recordLevelWon( { mines, clearedCells, elapsedSeconds } )
{
    if ( !isStatsEnabled() ) return;

    addInt( KEY_CLEARED_FIELDS, clearedCells );
    addInt( KEY_MINES_DEFUSED, mines );
    addInt( KEY_PLAY_TIME, elapsedSeconds );
    addInt( KEY_LEVELS_WON, 1 );

    if ( getItem( KEY_CUR_FIRST_TRY ) === '1' )
    {
        addInt( KEY_FIRST_TRY_WINS, 1 );
    }

    // Clear per-level flags — the next level start will set them fresh.
    removeItem( KEY_CUR_LEVEL );
    removeItem( KEY_CUR_FIRST_TRY );
}

/**
 * Record a confirmed level loss (after any Continue was declined / used up).
 * clearedCells should be the number of cells the player had revealed BEFORE
 * the fatal click, so the auto reveal-all on death isn't counted.
 */
export function recordLevelLost( { clearedCells, elapsedSeconds } )
{
    if ( !isStatsEnabled() ) return;

    addInt( KEY_CLEARED_FIELDS, clearedCells );
    addInt( KEY_PLAY_TIME, elapsedSeconds );
    addInt( KEY_GAME_OVERS, 1 );

    // A failed attempt invalidates first-try status for the retry.
    setItem( KEY_CUR_FIRST_TRY, '0' );
}

/**
 * Wipe every stats counter and the per-level first-try flag back to zero.
 * Does NOT touch progression keys like 'minesweeper_cleared' — see getStats()
 * for the full list of read keys.
 */
export function resetStats()
{
    removeItem( KEY_CLEARED_FIELDS );
    removeItem( KEY_MINES_DEFUSED );
    removeItem( KEY_PLAY_TIME );
    removeItem( KEY_GAME_OVERS );
    removeItem( KEY_LEVELS_STARTED );
    removeItem( KEY_LEVELS_WON );
    removeItem( KEY_FIRST_TRY_WINS );
    removeItem( KEY_CUR_LEVEL );
    removeItem( KEY_CUR_FIRST_TRY );
}

/**
 * Snapshot of all stats for the stats screen.
 */
export function getStats()
{
    const clearedLevels = readInt( KEY_LEVEL_PROGRESS );
    const currentLevel  = Math.min( clearedLevels + 1, MAX_LEVEL );

    const clearedFields  = readInt( KEY_CLEARED_FIELDS );
    const minesDefused   = readInt( KEY_MINES_DEFUSED );
    const totalPlayTime  = readInt( KEY_PLAY_TIME );
    const totalGameOvers = readInt( KEY_GAME_OVERS );
    const levelsStarted  = readInt( KEY_LEVELS_STARTED );
    const levelsWon      = readInt( KEY_LEVELS_WON );
    const firstTryWins   = readInt( KEY_FIRST_TRY_WINS );

    // Success ratio counts levels actually completed, not the level currently
    // in progress. currentLevel = cleared + 1, so wins so far is currentLevel - 1.
    const wins = Math.max( 0, currentLevel - 1 );
    const successRatio    = levelsStarted > 0 ? wins          / levelsStarted : 0;
    const firstTryRatio   = levelsStarted > 0 ? firstTryWins  / levelsStarted : 0;
    const avgTimePerLevel = levelsStarted > 0 ? totalPlayTime / levelsStarted : 0;

    return {
        currentLevel,
        clearedFields,
        minesDefused,
        totalPlayTime,
        totalGameOvers,
        levelsStarted,
        levelsWon,
        firstTryWins,
        successRatio,
        firstTryRatio,
        avgTimePerLevel,
    };
}
